import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import msrpValueHandler from "@tIronValleySupply/Handlers/MsrpValueHandler";
import ProductPartNumbers from "@tIronValleySupply/Overrides/Components/ProductPartNumbers";
import CustomApplicationState from "@tIronValleySupply/Store/Data/CustomApplicationState/CustomApplicationState";
import React from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    session: getSession(state),
    productSettings: getSettingsCollection(state).productSettings,
    isAutheticated: getSession(state).isAuthenticated,
    msrpValue: state.IronValleyData.MsrpValueStore.msrpValue,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    labelTextNormal?: TypographyProps;
    valueTextNormal?: TypographyProps;
    valueTextWarp?: TypographyProps;
    labelTextDealerNormal?: TypographyProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    brandStyles: {
        nameLink: {
            css: css`
                span {
                    font-weight: 600;
                    font-size: 14.7444px;
                    line-height: 18px;
                    text-transform: uppercase;
                    color: #000;
                }
            `,
        },
        nameText: {
            css: css`
                span {
                    font-weight: 600;
                    font-size: 14.7444px;
                    line-height: 18px;
                    text-transform: uppercase;
                    color: #000;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                min-height: 54px;
                height: auto;
                margin-bottom: 30px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            height: auto;
                        `,
                        css`
                            height: auto;
                        `,
                        css`
                            height: auto;
                        `,
                        css`
                            height: auto;
                        `,
                    ])}
                > span {
                    font-weight: 600;
                    font-size: 18.4305px;
                    line-height: 31px;
                    letter-spacing: 0.01em;
                    text-transform: capitalize;
                    color: #000;
                    margin-bottom: 0;
                }
            `,
            typographyProps: {
                size: 20,
                css: css`
                    margin-bottom: 0;
                    font-weight: 600;
                    font-size: 18.4305px;
                    line-height: 31px;
                    letter-spacing: 0.01em;
                    text-transform: capitalize;
                    color: #000;
                `,
            },
        },
    },
    brandGridItem: {
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            padding-top: 0;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 22px;
        `,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },

    labelTextNormal: {
        css: css`
            text-transform: uppercase;
            font-weight: 600;
            font-size: 12px;
            line-height: 17px;
            margin-right: 0;
            color: #252525;
            width: 36px;
        `,
    },
    labelTextDealerNormal: {
        css: css`
            text-transform: uppercase;
            font-weight: 600;
            font-size: 12px;
            line-height: 17px;
            margin-right: 0;
            color: #252525;
            width: 172px;
        `,
    },
    valueTextWarp: {
        size: 16,
        css: css`
            margin-top: 15px;
            display: flex;
            align-items: center;
        `,
    },
    valueTextNormal: {
        css: css`
            font-weight: 700;
            font-size: 12px;
            line-height: 19px;
            color: #e13e28;
            margin-left: 8px;
        `,
    },
    partNumbersStyles: {
        erpNumberLabelText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
        erpNumberValueText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
        customerNameLabelText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
        customerNameValueText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
        manufacturerItemValueText: {
            css: css`
                font-weight: 600;
                font-size: 11.7564px;
                line-height: 17px;
                text-transform: capitalize;
            `,
        },
    },
};

const styles = productInformationStyles;

const ProductListProductInformationListView = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    productSettings,
    session,
    isAutheticated,
    msrpValue,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    let msrpValueToDisplay: string | null = "false";

    if (typeof window !== "undefined") {
        const storedValue = localStorage.getItem("msrpValue");
        msrpValueToDisplay = storedValue !== null ? storedValue : "false";
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    const isAllocatedProduct = product?.properties?.allocated_Product
        ? product.properties.allocated_Product.toLowerCase()
        : "";

    let isStirkeOutPrice = "";

    if (
        productContext.productInfo.pricing &&
        productContext.productInfo.pricing.unitRegularPrice < productContext.productInfo.pricing.unitListPrice
    ) {
        isStirkeOutPrice = productContext.productInfo.pricing.unitListPriceDisplay;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.brandGridItem}>
                {product.brand && showBrand && (
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                )}
            </GridItem>

            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        upcNumber={parseInt(product?.detail?.upcCode || "")}
                        showCustomerName={false}
                        showManufacturerItem={true}
                        showUpcNumber={true}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}

            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformationListView));
